import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { client } from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { PrayerRequestDialog } from "@/components/PrayerRequestDialog";
import { PrayWithDialog } from "@/components/PrayWithDialog";
import { HandHeart, Users, Loader2, HeartHandshake, Sparkles } from "lucide-react";

const ago = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function PrayerWallPage() {
  const [requests, setRequests] = useState(null);
  const [stats, setStats] = useState(null);
  const [praying, setPraying] = useState({});
  const [askOpen, setAskOpen] = useState(false);
  const [prayFor, setPrayFor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const mountedOnce = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/prayer/wall");
      const reqs = data.requests || [];
      setRequests(reqs);
      setStats(data.my_pray_stats || null);
      if (reqs.some((r) => r.is_own && r.new_prayers > 0)) client.post("/prayer/ack").catch(() => {});
    } catch (e) { setRequests([]); }
  }, []);

  useEffect(() => {
    if (mountedOnce.current) return;
    mountedOnce.current = true;
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get("ask") === "1") {
      setAskOpen(true);
      const p = new URLSearchParams(searchParams);
      p.delete("ask");
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pray = async (id) => {
    if (praying[id]) return;
    setPraying((p) => ({ ...p, [id]: true }));
    try {
      const { data } = await client.post(`/prayer/${id}/pray`);
      setRequests((rs) => rs.map((r) => r.id === id ? { ...r, pray_count: data.pray_count, prayed_by_me: true } : r));
    } catch (e) { /* ignore */ }
    setPraying((p) => ({ ...p, [id]: false }));
  };

  const mine = (requests || []).filter((r) => r.is_own);
  const others = (requests || []).filter((r) => !r.is_own);

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="prayer-wall-page">
      <div className="rounded-3xl bg-brand-navy text-brand-ivory border border-brand-gold/20 p-8 shadow-soft">
        <div className="rounded-xl bg-brand-teal/20 p-2.5 w-fit mb-4"><Users className="w-6 h-6 text-brand-teal" /></div>
        <p className="uppercase tracking-[0.2em] text-[11px] text-gold mb-2">Together</p>
        <h1 className="font-serif text-3xl sm:text-4xl mb-3">The Prayer Wall</h1>
        <p className="text-brand-ivory-muted leading-relaxed">
          No one breaks free alone. When someone here is fighting a craving, you can stand with them in prayer —
          and when it's you, others will stand with you. "Carry each other's burdens." (Galatians 6:2)
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {stats?.streak >= 2 && (
            <div data-testid="pray-streak-badge" className="inline-flex items-center gap-2 rounded-full bg-brand-teal/15 text-brand-teal px-4 py-2 text-sm font-semibold">
              <Sparkles className="w-4 h-4" /> {stats.streak} days praying with others
            </div>
          )}
          {requests !== null && mine.length === 0 && (
            <button data-testid="ask-for-prayer-btn" onClick={() => setAskOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal text-brand-navy px-5 py-3 text-sm font-semibold hover:bg-brand-teal/90 transition-colors">
              <HandHeart className="w-4 h-4" /> Ask for prayer
            </button>
          )}
        </div>
      </div>

      {requests === null ? (
        <div className="flex items-center gap-2 text-stone py-10 justify-center" data-testid="wall-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Gathering the wall…
        </div>
      ) : (
        <>
          {mine.map((r) => (
            <div key={r.id} data-testid="my-prayer-request"
              className="rounded-3xl bg-brand-teal/5 border border-brand-teal/40 p-6 shadow-soft">
              <p className="text-[11px] uppercase tracking-[0.15em] text-brand-teal-dark mb-1">Your request</p>
              <p className="font-serif text-lg text-olive mb-2">You're facing {r.context}.</p>
              <p className="text-brand-teal-dark font-medium" data-testid="my-pray-count">
                {r.pray_count === 0
                  ? "Your request is up — others will pray with you soon."
                  : `${r.pray_count} ${r.pray_count === 1 ? "person is" : "people are"} praying with you.`}
              </p>
              {r.new_prayers > 0 && (
                <div data-testid="my-prayer-nudge" className="mt-3 flex items-start gap-2 rounded-2xl bg-brand-navy text-brand-ivory p-4">
                  <HeartHandshake className="w-4 h-4 text-brand-teal mt-0.5 shrink-0" />
                  <p className="text-sm leading-relaxed">
                    {r.new_prayers === 1
                      ? "Someone just prayed with you since you were last here."
                      : `${r.new_prayers} people just prayed with you since you were last here.`} You're not alone.
                  </p>
                </div>
              )}
            </div>
          ))}

          {others.length === 0 && mine.length === 0 && (
            <div className="rounded-3xl bg-card border border-border p-10 text-center" data-testid="wall-empty">
              <div className="rounded-2xl bg-sage w-14 h-14 flex items-center justify-center mx-auto mb-4">
                <HandHeart className="w-6 h-6 text-brand-teal-dark" />
              </div>
              <p className="font-serif text-xl text-olive mb-1">All quiet for now</p>
              <p className="text-olive/70">When someone asks for prayer, they'll appear here. Check back and lift them up.</p>
            </div>
          )}

          <div className="space-y-3">
            {others.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                className="rounded-3xl bg-card border border-border p-5 shadow-soft flex items-center gap-4" data-testid={`prayer-card-${r.id}`}>
                <div className="rounded-full bg-sage w-11 h-11 flex items-center justify-center shrink-0 font-serif text-lg text-brand-teal-dark">
                  {r.anonymous ? <HandHeart className="w-5 h-5" /> : (r.name?.[0]?.toUpperCase() || "?")}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-olive"><span className="font-medium">{r.name}</span> is facing {r.context}.</p>
                  <p className="text-xs text-stone mt-0.5">
                    {ago(r.created_at)}{r.pray_count > 0 ? ` · ${r.pray_count} praying` : ""}
                  </p>
                </div>
                <button data-testid={`pray-btn-${r.id}`} onClick={() => setPrayFor(r)}
                  className={`shrink-0 rounded-full px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    r.prayed_by_me
                      ? "bg-brand-teal/15 text-brand-teal-dark"
                      : "bg-brand-teal text-brand-navy hover:bg-brand-teal/90"}`}>
                  <HandHeart className="w-4 h-4" /> {r.prayed_by_me ? "Praying" : "Pray with them"}
                </button>
              </motion.div>
            ))}
          </div>

          {(others.length > 0 || mine.length > 0) && (
            <p className="text-xs text-stone text-center pt-2">Requests rest here for 72 hours. Names are never shared beyond a first name.</p>
          )}
        </>
      )}

      <PrayWithDialog
        request={prayFor}
        open={!!prayFor}
        onOpenChange={(v) => { if (!v) setPrayFor(null); }}
        onPray={() => pray(prayFor.id)}
        praying={prayFor ? !!praying[prayFor.id] : false}
      />

      <PrayerRequestDialog open={askOpen} onOpenChange={setAskOpen} onCreated={() => load()} />
    </div>
  );
}
